import Flex from "common/Boxes/Flex";
import ExclamationCircleOutlined from "@ant-design/icons/ExclamationCircleOutlined";
import Text from "common/Typography/Text";
import Button from "common/Buttons/Button";
import Modal from "common/Modal/Modal";
import React from "react";
import { DCA_PUBLIC_BOTS_PATH } from "common/constants";
import styled, { useTheme } from "styled-components";
import { TriangleAlert } from "lucide-react";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import { useHistory } from "react-router-dom";
import {
  getPublicBotErrorMessage,
  getPublicBotErrorTitle,
} from "@shared/features/bots/utils";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";

interface InvalidPublicBotModalProps {
  publicBotErrors?: {
    isInvalidLink: boolean;
    isPrivate: boolean;
    generalError: boolean;
  };
}

const InvalidPublicBotModal = ({
  publicBotErrors,
}: InvalidPublicBotModalProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const theme = useTheme();
  const history = useHistory();

  const onTakeMeBackClick = () => {
    history.push(DCA_PUBLIC_BOTS_PATH);
  };

  const hasError = Object.values(publicBotErrors || {}).some(
    (isError) => isError
  );

  if (theme.isNewLook) {
    // New look: a left-aligned title with the warning mark, the message in
    // the body role and the one action bottom-right.
    return (
      <Modal
        open={hasError}
        title={
          <NewLookTitle>
            <TriangleAlert
              size={20}
              strokeWidth={2}
              color={getNewLookTokens(theme).stop}
              aria-hidden={true}
            />
            {getPublicBotErrorTitle(t, publicBotErrors)}
          </NewLookTitle>
        }
        footer={null}
        closable={false}
        centered={true}
      >
        <NewLookBody>
          {getPublicBotErrorMessage(t, publicBotErrors)}
        </NewLookBody>
        <NewLookFooter>
          <Button type="primary" onClick={onTakeMeBackClick}>
            {t("public_bots.take_me_back")}
          </Button>
        </NewLookFooter>
      </Modal>
    );
  }

  return (
    <Modal
      open={hasError}
      title={
        <Flex $justifyContent="flex-start">
          <ExclamationCircleOutlined
            style={{
              fontSize: 24,
              color: theme.colors.gold,
              marginRight: 12,
            }}
          />
          {getPublicBotErrorTitle(t, publicBotErrors)}
        </Flex>
      }
      footer={null}
      closable={false}
      centered={true}
    >
      <Text>{getPublicBotErrorMessage(t, publicBotErrors)}</Text>
      <Flex $justifyContent="center" style={{ marginTop: 20, width: "100%" }}>
        <Button
          type="primary"
          onClick={onTakeMeBackClick}
          style={{ width: 170 }}
        >
          {t("public_bots.take_me_back")}
        </Button>
      </Flex>
    </Modal>
  );
};

export default InvalidPublicBotModal;

const NewLookTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;
const NewLookBody = styled.p`
  margin: 0;
  ${newLookType.body};
  color: ${(p) => getNewLookTokens(p.theme).text};
`;
const NewLookFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
`;
